import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/base-components';
import { useSoldoutEvents, useBulkJoinWaitlist, useBulkJoinJobs, useBulkJoinJob, useCancelBulkJoinJob, useAccounts, useAccountGroups, useMarkWaitlistJoined, useFetchWaitlistEventStatus } from '../../hooks/useApi';
import { AccountGroupChip } from '../components/AccountGroupChip';
import { useToast } from '../../contexts/ToastContext';
import { emptyWaitlistLookup, useWaitlistLookups } from '../../components/WaitlistStatusLookup';
import { planSmartJoin } from '../../components/waitlistStatus';
import { MyWaitlistsSection } from '../../components/MyWaitlistsSection';
import { WaitlistClaimCode } from '../../components/WaitlistClaimCode';
import { AccountGroup, SoldoutEvent, SoldoutTicketType, BulkJoinWaitlistRequest, BulkJoinJob, ExternalAccount, WaitlistEventStatusResponse } from '../../types';
import {
  Badge,
  EmptyState,
  PageHeader,
  PageShell,
  SegmentedControl,
  SkeletonCard,
  cx,
} from '../components';
import { ChevronDownIcon, ListIcon, SearchIcon } from '../components/icons';
import { FOCUS_RING, FOCUS_RING_SURFACE } from '../components/styles';
import { v2Path } from '../nav';
import { accountIdentity, accountPhone } from '../../components/accountIdentity';

const formatPrice = (amount: number, currency: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount / 100);
};

// Per docs/waitlist-endpoints.md, event_start_date is ISO 8601 with the
// venue's UTC offset embedded (e.g. "2026-02-25T14:00:00-05:00"). The
// wall-clock prefix IS the venue-local time. Calling `new Date(...)` here
// would produce a real UTC moment that toLocaleDateString then renders in
// the BROWSER'S tz — so a NY event reads differently for a CA viewer. We
// pull the components straight from the string and render with
// timeZone: 'UTC' so the displayed wall clock always matches the venue.
const formatVenueLocalDate = (isoStr: string | null | undefined): string => {
  if (!isoStr) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(isoStr);
  if (!m) return isoStr;
  const [, y, mo, d, h = '0', mi = '0', s = '0'] = m;
  const dt = new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const getQuantityOptions = (ticketType: SoldoutTicketType, maxTickets: number | null) => {
  const options = Array.from(
    { length: ticketType.max_increments },
    (_, i) => (i + 1) * ticketType.increment
  );
  if (maxTickets) {
    return options.filter(q => q <= maxTickets);
  }
  return options;
};

const isWaitlistOpen = (tt: SoldoutTicketType) => tt.secondary_status === 'waiting-list';
const openTicketTypes = (event: SoldoutEvent) => event.ticket_types.filter(isWaitlistOpen);

/** The one place ticket counts get worded, so every surface reads the same. */
const ticketsLabel = (q: number) => `${q} ticket${q > 1 ? 's' : ''}`;

/**
 * A default quantity is a wish, not a guarantee: each tier has its own
 * `increment` / `max_increments`, and the event may cap `max_tickets`. Snap the
 * wish to the closest allowed option at or below it, so what the button says is
 * always exactly what the payload carries.
 */
const resolveQuantity = (options: number[], wanted: number): number => {
  if (options.length === 0) return wanted;
  if (options.includes(wanted)) return wanted;
  const below = options.filter(o => o <= wanted);
  return below.length > 0 ? below[below.length - 1] : options[0];
};

// ── Remembered join defaults ─────────────────────────────────────────────
// The diagnosed problem: state was purely per-event, so joining ten drops meant
// answering "all or some accounts?" and "how many tickets?" ten times each. The
// answers almost never change between events, so we remember the last ones and
// treat them as the default for every event that has no explicit override.

/**
 * `group` is "Remaining Accounts" scoped to one account group: the group's
 * usable members, minus whoever is already on the list, at one quantity. It
 * sits beside the other two because picking a group and a number is the whole
 * ask for most sellers — the checkbox grid in `select` is for fine-tuning.
 */
type JoinMode = 'unjoined' | 'group' | 'select';

interface JoinDefaults {
  mode: JoinMode;
  quantity: number;
  /** Only consulted in `select` mode; filtered against live accounts on use. */
  accountIds: number[];
  /** Only consulted in `group` mode; a deleted group falls back to none. */
  groupId: number | null;
}

const JOIN_DEFAULTS_STORAGE_KEY = 'flashtools_waitlist_join_defaults';

type GroupPick = { group: AccountGroup; ids: number[] };

/**
 * One row of group chips with exactly one lit. The count on each chip is the
 * number of usable DICE members — the ceiling the join button then promises.
 * With no groups there is nothing to pick, so it says where to make one.
 */
const GroupPicker: React.FC<{
  picks: GroupPick[];
  selectedId: number | null;
  onPick: (id: number) => void;
  accountsPath: string;
}> = ({ picks, selectedId, onPick, accountsPath }) => {
  const navigate = useNavigate();
  if (picks.length === 0) {
    return (
      <p className="text-xs text-amber-400/80">
        No groups with active DICE accounts yet.{' '}
        <button
          type="button"
          onClick={() => navigate(accountsPath)}
          className={cx('rounded text-[#60a5fa] hover:underline', FOCUS_RING)}
        >
          Create one on Accounts
        </button>
      </p>
    );
  }
  return (
    <div role="group" aria-label="Group to join with" className="flex flex-wrap items-center gap-2">
      {picks.map(({ group, ids }) => (
        <AccountGroupChip
          key={group.id}
          group={group}
          count={ids.length}
          active={group.id === selectedId}
          title={`Join with the ${ids.length} DICE account${ids.length !== 1 ? 's' : ''} in ${group.name}`}
          onClick={() => onPick(group.id)}
        />
      ))}
    </div>
  );
};

/** Joining the unjoined accounts is the default: it is what "join everything"
 *  means almost every time, and the only mode that cannot replace an entry an
 *  account already holds. */
const SEED_JOIN_DEFAULTS: JoinDefaults = { mode: 'unjoined', quantity: 1, accountIds: [], groupId: null };

const readJoinDefaults = (): JoinDefaults => {
  try {
    const raw = localStorage.getItem(JOIN_DEFAULTS_STORAGE_KEY);
    if (!raw) return SEED_JOIN_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<JoinDefaults> | null;
    const quantity = Number(parsed?.quantity);
    return {
      // 'all' and 'smart' are retired names; both land on 'unjoined'.
      mode: parsed?.mode === 'select' || parsed?.mode === 'group' ? parsed.mode : 'unjoined',
      quantity: Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1,
      accountIds: Array.isArray(parsed?.accountIds)
        ? parsed!.accountIds.filter((id): id is number => typeof id === 'number')
        : [],
      groupId: typeof parsed?.groupId === 'number' ? parsed.groupId : null,
    };
  } catch {
    return SEED_JOIN_DEFAULTS;
  }
};

const writeJoinDefaults = (defaults: JoinDefaults) => {
  try {
    localStorage.setItem(JOIN_DEFAULTS_STORAGE_KEY, JSON.stringify(defaults));
  } catch {
    /* private mode / quota — defaults simply stay session-only */
  }
};

/** Shared `<select>` treatment — v1 used raw selects and so does this. */
const SELECT_CLASS = cx(
  'rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white cursor-pointer',
  FOCUS_RING,
);

// Per-event job status banner — owns its own polling hook
const JobStatusBanner: React.FC<{
  jobId: number;
  onProgress: (job: BulkJoinJob) => void;
  onComplete: (job: BulkJoinJob) => void;
  onCancel: (job: BulkJoinJob) => void;
}> = ({ jobId, onProgress, onComplete, onCancel }) => {
  const { showSuccess, showError } = useToast();
  const cancelJobMutation = useCancelBulkJoinJob();
  const { data: job } = useBulkJoinJob(jobId, { refetchInterval: 1000 });

  // Every poll, not just the terminal one: accounts join one at a time and
  // each badge should land as its account does, not all at the end.
  useEffect(() => {
    if (job) onProgress(job);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.processed, job?.successful_count]);

  useEffect(() => {
    if (!job) return;
    if (job.status === 'completed') {
      onComplete(job);
    } else if (job.status === 'failed' || job.status === 'cancelled') {
      onCancel(job);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status]);

  if (!job || job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
    return null;
  }

  const progress = job.total_accounts > 0
    ? `${job.processed}/${job.total_accounts}`
    : '';

  return (
    <div className="flex items-center gap-2 rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2">
      <div className="h-3 w-3 flex-shrink-0 animate-pulse rounded-full bg-amber-400" />
      <span className="flex-1 text-sm text-amber-300 tabular-nums">
        Job running{progress ? ` (${progress})` : '...'}
      </span>
      <button
        type="button"
        onClick={async () => {
          try {
            const result = await cancelJobMutation.mutateAsync(jobId);
            if (result.status === 'completed') {
              // Finished between the last poll and the click: nothing to cancel.
              showSuccess(`Job already finished. ${result.successful_count} joined.`);
              onComplete(result);
              return;
            }
            const msg = result.successful_count > 0
              ? `Job cancelled. ${result.successful_count} already processed.`
              : 'Job cancelled.';
            showSuccess(msg);
            onCancel(result);
          } catch (err: any) {
            showError(err.message || 'Failed to cancel job');
          }
        }}
        disabled={cancelJobMutation.isPending}
        className={cx(
          'flex-shrink-0 rounded border border-red-500/30 bg-red-500/20 px-2 py-0.5 text-xs text-red-400 transition-colors hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-50',
          FOCUS_RING,
        )}
      >
        {cancelJobMutation.isPending ? 'Cancelling...' : 'Cancel'}
      </button>
    </div>
  );
};

export const WaitlistScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [waitlistOnly, setWaitlistOnly] = useState(false);
  const [subscribedOnly, setSubscribedOnly] = useState(false);
  const [cityFilter, setCityFilter] = useState<string>('');

  // Remembered answers to the two questions v1 asked on every single event.
  const [joinDefaults, setJoinDefaults] = useState<JoinDefaults>(readJoinDefaults);
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  // Join flow state (per-event OVERRIDES, keyed by event_id).
  // `undefined` is meaningful everywhere below: it means "this event has no
  // opinion, use the remembered default".
  const [selectedTicketType, setSelectedTicketType] = useState<Record<string, number | undefined>>({});
  const [selectedQuantity, setSelectedQuantity] = useState<Record<string, number | undefined>>({});

  // Bulk join state
  const [joinMode, setJoinMode] = useState<Record<string, JoinMode | undefined>>({});
  const [joinGroup, setJoinGroup] = useState<Record<string, number | undefined>>({});
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, Set<number> | undefined>>({});
  const [perAccountQty, setPerAccountQty] = useState<Record<string, Record<number, number> | undefined>>({});
  // Which event cards have their per-event override panel open
  const [expandedEvents, setExpandedEvents] = useState<Record<string, boolean | undefined>>({});
  // Events whose account status we've fetched. One request per event, so only
  // events the customer has actually opened join it — and they stay in once
  // added, or collapsing the panel would silently restore accounts the picker
  // had just unchecked for already holding tickets.
  const [statusEventIds, setStatusEventIds] = useState<string[]>([]);
  /** Per-row ticket ownership, hidden until asked for. Screen-level so it
   *  does not reset every time a different event's picker is opened. */
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  /** Open warning dialog: a join that would replace entries, awaiting a call. */
  const [joinConfirm, setJoinConfirm] = useState<{
    eventId: string;
    /** Accounts the customer picked that already hold an entry. Never empty:
     *  the dialog exists only to warn that joining replaces those entries. */
    onWaitlist: number[];
    eligible: number[];
  } | null>(null);
  // Active jobs mapped by event_id
  const [activeJobIds, setActiveJobIds] = useState<Record<string, number>>({});

  // Data fetching
  const { data: events = [], isLoading } = useSoldoutEvents({
    waitlist: waitlistOnly || undefined,
    subscribed: subscribedOnly || undefined,
    city: cityFilter || undefined,
  });
  const { data: allEvents = [] } = useSoldoutEvents();
  const { data: accounts = [] } = useAccounts();
  const { data: allJobs = [] } = useBulkJoinJobs();
  const bulkJoinMutation = useBulkJoinWaitlist();
  const markWaitlistJoined = useMarkWaitlistJoined();
  const fetchWaitlistEventStatus = useFetchWaitlistEventStatus();
  // What the in-flight job is joining, per event. The job's items say which
  // accounts got on; the tier they got on for lives here.
  const pendingJoinRef = useRef<Record<string, {
    ticketTypeId: number | null;
    ticketTypeName: string | null;
    /** Accounts the job was given, in case it reports no per-account items. */
    accountIds: number[];
    quantityFor: (accountId: number) => number;
  }>>({});
  /** Accounts already badged from this job, so 500ms polling doesn't re-badge. */
  const badgedRef = useRef<Record<string, Set<number>>>({});
  /** Read inside a job callback, which must not re-subscribe as accounts load. */
  const diceAccountsRef = useRef<ExternalAccount[]>([]);

  /**
   * Badges accounts the job reports as actually joined, as it reports them.
   *
   * Submitting only queues a job — badging at that point claims a place on the
   * list before anything has been asked of Dice, and the badge would be a lie
   * for every account the job later fails on. Only a `success` item earns it.
   * `skipped` does not: the account was passed over, and whatever entry it may
   * already hold is the sync's business to report, not ours to invent.
   */
  const badgeJoinedAccounts = useCallback((eventId: string, job: BulkJoinJob) => {
    const pending = pendingJoinRef.current[eventId];
    if (!pending) return;

    const badged = badgedRef.current[eventId] ?? new Set<number>();
    const items = job.items || [];

    // Preferred path: the job says exactly which accounts got on.
    let joined = items
      .filter(item => item.status === 'success' && !badged.has(item.account_id))
      .map(item => ({ accountId: item.account_id, numberOfTickets: item.number_of_tickets }));

    // Fallback for a job that reports counts but no breakdown. Attribution is
    // only safe when every account succeeded — a partial result names no
    // names, and guessing which ones would badge accounts that never joined.
    const finished = job.status === 'completed' || job.status === 'cancelled' || job.status === 'failed';
    if (items.length === 0 && finished && job.successful_count === pending.accountIds.length) {
      joined = pending.accountIds
        .filter(id => !badged.has(id))
        .map(accountId => ({ accountId, numberOfTickets: pending.quantityFor(accountId) }));
    }

    if (joined.length === 0) return;

    markWaitlistJoined(
      eventId,
      joined.map(({ accountId, numberOfTickets }) => {
        const acct = diceAccountsRef.current.find(a => a.id === accountId);
        return {
          accountId,
          accountLabel: (acct && accountPhone(acct)) || acct?.email || `Account ${accountId}`,
          ticketTypeId: pending.ticketTypeId,
          ticketTypeName: pending.ticketTypeName,
          numberOfTickets,
        };
      }),
    );

    joined.forEach(({ accountId }) => badged.add(accountId));
    badgedRef.current[eventId] = badged;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeJoinDefaults(joinDefaults);
  }, [joinDefaults]);

  // Seed active jobs from the jobs list on load
  useEffect(() => {
    const active: Record<string, number> = {};
    for (const job of allJobs) {
      if ((job.status === 'pending' || job.status === 'processing') && job.event_id) {
        active[job.event_id] = job.job_id;
      }
    }
    setActiveJobIds(prev => {
      // Merge: keep locally-added jobs that may not be in the list yet
      const merged = { ...active };
      for (const [eventId, jobId] of Object.entries(prev)) {
        if (!merged[eventId]) merged[eventId] = jobId;
      }
      return merged;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allJobs]);

  const handleJobComplete = useCallback((eventId: string) => (job: BulkJoinJob) => {
    setActiveJobIds(prev => {
      const next = { ...prev };
      delete next[eventId];
      return next;
    });

    badgeJoinedAccounts(eventId, job);
    delete pendingJoinRef.current[eventId];
    delete badgedRef.current[eventId];

    const failed = job.failed_count + job.skipped_count;
    if (failed > 0) {
      showSuccess(`Job finished: ${job.successful_count} succeeded, ${failed} failed`);
    } else {
      showSuccess(`Job finished: all ${job.successful_count} accounts joined successfully`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJobCancel = useCallback((eventId: string) => (job: BulkJoinJob) => {
    setActiveJobIds(prev => {
      const next = { ...prev };
      delete next[eventId];
      return next;
    });

    // A cancelled or failed job still reports items, and some may have gone
    // through before it stopped. Those accounts are on the list and get badged.
    badgeJoinedAccounts(eventId, job);
    delete pendingJoinRef.current[eventId];
    delete badgedRef.current[eventId];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const diceAccounts = useMemo(
    () => accounts.filter(a => a.platform === 'DICE' && a.is_active),
    [accounts]
  );
  diceAccountsRef.current = diceAccounts;

  const diceAccountIds = useMemo(
    () => new Set(diceAccounts.map(a => a.id)),
    [diceAccounts]
  );

  /**
   * Account groups as quick picks for Select mode. A group's usable members
   * here are the same set the server would join for it: active, unbanned DICE
   * accounts — so the count on the chip is the count that gets joined.
   */
  const { data: accountGroups = [] } = useAccountGroups();
  const groupPicks = useMemo(() => {
    const banned = new Set(diceAccounts.filter(a => a.is_banned).map(a => a.id));
    return accountGroups
      .map(g => ({
        group: g,
        ids: g.member_ids.filter(id => diceAccountIds.has(id) && !banned.has(id)),
      }))
      .filter(p => p.ids.length > 0);
  }, [accountGroups, diceAccounts, diceAccountIds]);
  const groupPickById = (id: number | null | undefined) =>
    id == null ? undefined : groupPicks.find(p => p.group.id === id);
  /** Remembered group, if it still exists and still has usable members. */
  const defaultGroupPick = groupPickById(joinDefaults.groupId);

  /**
   * Every connected Dice account, for the claim box on a browse card.
   *
   * Deliberately not narrowed to accounts already on the waitlist, as the "My
   * waitlists" version is: our copy of who is on a waitlist is only as fresh as
   * the last account sync, and a seller holding a real code should never be
   * told the account it was sent to doesn't exist.
   */
  const claimAccountOptions = useMemo(
    () => diceAccounts.map(a => ({ id: a.id, label: accountIdentity(a) })),
    [diceAccounts]
  );

  /** Remembered ids minus any account that has since been removed or deactivated. */
  const defaultAccountIds = useMemo(
    () => joinDefaults.accountIds.filter(id => diceAccountIds.has(id)),
    [joinDefaults.accountIds, diceAccountIds]
  );

  /** Per-event account status: the badges, and the default-selection rule. */
  const waitlistLookups = useWaitlistLookups(statusEventIds);
  const lookupFor = (eventId: string) => waitlistLookups.get(eventId) ?? emptyWaitlistLookup();

  const cities = useMemo(() => {
    const citySet = new Set<string>();
    allEvents.forEach(e => {
      if (e.venue_city) citySet.add(e.venue_city);
    });
    return Array.from(citySet).sort();
  }, [allEvents]);

  /**
   * The default quantity is global but the allowed quantities are per tier, so
   * offer the union of everything any open tier allows. No extra fetch — this
   * is the `allEvents` response the city filter already reads.
   */
  const defaultQuantityOptions = useMemo(() => {
    const set = new Set<number>();
    allEvents.forEach(e => {
      e.ticket_types.forEach(tt => {
        if (isWaitlistOpen(tt)) getQuantityOptions(tt, e.max_tickets).forEach(q => set.add(q));
      });
    });
    if (set.size === 0) [1, 2, 3, 4].forEach(q => set.add(q));
    set.add(joinDefaults.quantity);
    return Array.from(set).sort((a, b) => a - b);
  }, [allEvents, joinDefaults.quantity]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = !q
      ? events
      : events.filter(e =>
          e.name.toLowerCase().includes(q) ||
          e.venue_name?.toLowerCase().includes(q) ||
          e.promoter_name?.toLowerCase().includes(q)
        );
    // Joinable first, then soonest — the whole point of the screen is the join,
    // so events you cannot join sink below the ones you can.
    return [...matched].sort((a, b) => {
      const aOpen = openTicketTypes(a).length > 0 ? 0 : 1;
      const bOpen = openTicketTypes(b).length > 0 ? 0 : 1;
      if (aOpen !== bOpen) return aOpen - bOpen;
      const aTime = Date.parse(a.event_start_date);
      const bTime = Date.parse(b.event_start_date);
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
      return aTime - bTime;
    });
  }, [events, searchQuery]);

  /**
   * The single resolver every surface goes through — the collapsed row's label,
   * the expanded panel's controls, and the request builder all read from here,
   * so the button can never promise something the payload does not carry.
   */
  const resolvePlan = (event: SoldoutEvent) => {
    const eventId = event.event_id;
    const openTypes = openTicketTypes(event);
    const explicitTt = event.ticket_types.find(
      tt => tt.id === selectedTicketType[eventId] && isWaitlistOpen(tt)
    );
    const ticketType = explicitTt || openTypes[0];
    const quantityOptions = ticketType ? getQuantityOptions(ticketType, event.max_tickets) : [];
    // New events start on unjoined accounts whatever the remembered default
    // says — it is the mode that cannot join an account twice.
    // The remembered mode carries over for `group` too: the group is the
    // seller's standing answer, and this mode also cannot join an account twice.
    const mode: JoinMode = joinMode[eventId] ?? (joinDefaults.mode === 'group' ? 'group' : 'unjoined');
    const isUnjoinedMode = mode === 'unjoined';
    const groupPick = mode === 'group'
      ? groupPickById(joinGroup[eventId] ?? joinDefaults.groupId)
      : undefined;
    const quantity = resolveQuantity(
      quantityOptions,
      selectedQuantity[eventId] ?? joinDefaults.quantity
    );
    // An explicit empty Set means "the user unchecked everything here", which is
    // NOT the same as "this event has no opinion" — only `undefined` falls back.
    //
    // The fallback drops accounts already holding tickets for this event: a
    // second pair is rarely what's wanted, and the picker shows the same rows
    // unchecked. An explicit selection is never filtered — checking such a row
    // is exactly how you override this.
    const lookup = waitlistLookups.get(eventId);
    const accountIds = mode === 'group'
      ? (groupPick?.ids ?? [])
      : selectedAccounts[eventId]
      ? Array.from(selectedAccounts[eventId] as Set<number>)
      : defaultAccountIds.filter(id => !lookup?.holdsTickets(id));
    const hasOverride =
      joinMode[eventId] !== undefined ||
      joinGroup[eventId] !== undefined ||
      selectedQuantity[eventId] !== undefined ||
      selectedAccounts[eventId] !== undefined ||
      perAccountQty[eventId] !== undefined;

    // Defaults trimmed for ticket ownership. Worth saying out loud: the count
    // on the join button silently shrank, and the customer never opened the
    // picker where the per-row badge explains why.
    const skippedWithTickets = mode !== 'select' || selectedAccounts[eventId]
      ? 0
      : defaultAccountIds.length - accountIds.length;

    return {
      openTypes,
      ticketType,
      quantityOptions,
      mode,
      isUnjoinedMode,
      groupPick,
      quantity,
      accountIds,
      skippedWithTickets,
      hasOverride,
    };
  };

  const clearOverrides = (eventId: string) => {
    const drop = <T,>(prev: Record<string, T>) => {
      const next = { ...prev };
      delete next[eventId];
      return next;
    };
    setJoinMode(drop);
    setJoinGroup(drop);
    setSelectedQuantity(drop);
    setSelectedAccounts(drop);
    setPerAccountQty(drop);
  };

  /**
   * Sends the join for exactly these accounts.
   *
   * Always an explicit `accounts` array, even in All-Accounts mode: the
   * server's implicit "all" means every active Dice account, which is no
   * longer what "join all" means here.
   */
  const submitBulkJoin = async (
    event: SoldoutEvent,
    plan: ReturnType<typeof resolvePlan>,
    accountIds: number[],
    skipNote?: string,
    /** Per-account amounts, already trimmed to what each has room for. */
    plannedQuantities?: Map<number, number>,
  ) => {
    const eventId = event.event_id;
    const tt = plan.ticketType;
    if (!tt) return;

    const quantityFor = (accountId: number) =>
      plannedQuantities?.get(accountId) ??
      (plan.mode === 'select'
        ? resolveQuantity(plan.quantityOptions, perAccountQty[eventId]?.[accountId] ?? plan.quantity)
        : plan.quantity);

    const request: BulkJoinWaitlistRequest = {
      event_id: eventId,
      ticket_type_id: tt.dice_ticket_id,
      event_name: event.name,
      ticket_type_name: tt.display_name,
      accounts: accountIds.map(accountId => ({
        account_id: accountId,
        // Same snap the per-account select displays, so the row and the payload
        // cannot disagree when a tier's increment does not admit the number.
        number_of_tickets: quantityFor(accountId),
      })),
    };

    try {
      const response = await bulkJoinMutation.mutateAsync(request);
      setActiveJobIds(prev => ({ ...prev, [eventId]: response.job_id }));

      // No badge yet: this call only queued a job. Accounts get badged as the
      // job reports each one joined — see badgeJoinedAccounts. All this records
      // is which tier those successes will be badged with.
      pendingJoinRef.current[eventId] = {
        ticketTypeId: tt.dice_ticket_id,
        ticketTypeName: tt.display_name,
        accountIds,
        quantityFor,
      };
      delete badgedRef.current[eventId];

      showSuccess(skipNote ? `${response.message} · ${skipNote}` : response.message);
      // "Last used" is literal: whatever this join actually sent becomes the
      // default for the next event, and the defaults card shows it immediately.
      setJoinDefaults(prev => ({
        mode: plan.mode,
        quantity: plan.quantity,
        // Only an explicit selection rewrites the remembered accounts. Accounts
        // this event skipped — for holding tickets, or for already being on the
        // list — were never deselected by the customer, and dropping them here
        // would carry that skip to every future event.
        accountIds:
          plan.mode !== 'select' || !selectedAccounts[eventId] ? prev.accountIds : plan.accountIds,
        groupId: plan.mode === 'group' ? plan.groupPick?.group.id ?? prev.groupId : prev.groupId,
      }));
    } catch (error: any) {
      showError(error.message || 'Failed to join waitlist');
    }
  };

  const handleBulkJoin = async (eventId: string) => {
    if (diceAccounts.length === 0) {
      navigate(v2Path('/accounts'));
      return;
    }

    const event = filteredEvents.find(e => e.event_id === eventId);
    if (!event) return;

    const plan = resolvePlan(event);
    if (!plan.ticketType) {
      showError('Please select a ticket type');
      return;
    }

    if (plan.mode === 'group' && !plan.groupPick) {
      showError('Pick a group to join with, or switch to Remaining Accounts');
      return;
    }
    const targeted = plan.mode === 'unjoined' ? diceAccounts.map(a => a.id) : plan.accountIds;
    if (targeted.length === 0) {
      showError('Please select at least one account');
      return;
    }

    // Who is already on the list decides the payload, so it is fetched before
    // sending rather than read off whatever the picker happened to render. A
    // status outage must not block joining, so failure falls through to the
    // old behaviour: send what was asked for.
    let status: WaitlistEventStatusResponse | null = null;
    try {
      status = await fetchWaitlistEventStatus(eventId);
    } catch {
      status = null;
    }

    if (!status) {
      await submitBulkJoin(event, plan, targeted);
      return;
    }

    const byAccount = new Map(status.accounts.map(a => [a.account_id, a]));
    const isOnWaitlist = (id: number) => {
      const entry = byAccount.get(id)?.waitlist;
      return !!entry && entry.status !== 'timed-out';
    };
    const onWaitlist = targeted.filter(isOnWaitlist);

    // Both the whole-inventory and the group flavour of "remaining" skip and
    // trim the same way; only the targeted set differs.
    if (plan.isUnjoinedMode || plan.mode === 'group') {
      const { quantities, atLimit } = planSmartJoin({
        targeted,
        accounts: byAccount,
        limit: event.max_tickets,
        wanted: plan.quantity,
        snap: (amount) => resolveQuantity(plan.quantityOptions, amount),
      });

      const eligible = Array.from(quantities.keys());
      const skipped = [
        onWaitlist.length > 0 ? `${onWaitlist.length} already on this waitlist` : null,
        atLimit.length > 0 ? `${atLimit.length} at the ticket limit` : null,
      ].filter(Boolean);

      // Nothing to do is an outcome, not a decision to escalate: this mode was
      // chosen precisely to skip these accounts, so offering to join them
      // anyway asks the customer to unpick what they just asked for.
      if (eligible.length === 0) {
        showError(
          skipped.length > 0
            ? `Nothing to join — ${skipped.join(' and ')}`
            : 'Nothing to join for this event',
        );
        return;
      }

      await submitBulkJoin(
        event,
        plan,
        eligible,
        skipped.length > 0 ? `skipped ${skipped.join(' and ')}` : undefined,
        quantities,
      );
      return;
    }

    // Select mode: the customer named these accounts. Joining one that already
    // holds an entry silently replaces its ticket type, so that gets a warning
    // rather than a silent skip.
    if (onWaitlist.length > 0) {
      setJoinConfirm({
        eventId,
        onWaitlist,
        eligible: targeted.filter(id => !isOnWaitlist(id)),
      });
      return;
    }

    await submitBulkJoin(event, plan, targeted);
  };

  /** Resolves the warning dialog with the set of accounts to actually join. */
  const resolveJoinConfirm = async (accountIds: number[]) => {
    const confirm = joinConfirm;
    setJoinConfirm(null);
    if (!confirm || accountIds.length === 0) return;

    const event = filteredEvents.find(e => e.event_id === confirm.eventId);
    if (!event) return;
    const plan = resolvePlan(event);
    if (!plan.ticketType) return;

    await submitBulkJoin(event, plan, accountIds);
  };

  const filterBtnClass = (active: boolean) =>
    cx(
      'cursor-pointer rounded-full border px-3 py-1.5 text-sm transition-all',
      active
        ? 'bg-[#3b82f6]/10 text-[#3b82f6] border-[#3b82f6]/30'
        : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10',
      FOCUS_RING,
    );

  const accountLabel = accountIdentity;

  const defaultsSummary = joinDefaults.mode === 'unjoined'
    ? `Remaining Accounts (up to ${diceAccounts.length})`
    : joinDefaults.mode === 'group'
    ? defaultGroupPick
      ? `Group: ${defaultGroupPick.group.name} (up to ${defaultGroupPick.ids.length})`
      : 'Group: none picked'
    : `Select Accounts (${defaultAccountIds.length})`;

  const segmentOptions: { value: JoinMode; label: string }[] = [
    { value: 'unjoined', label: 'Remaining Accounts' },
    { value: 'group', label: 'Group' },
    { value: 'select', label: 'Select Accounts' },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Waitlist"
        meta={
          !isLoading
            ? `${filteredEvents.length} event${filteredEvents.length !== 1 ? 's' : ''}`
            : undefined
        }
      />

      {/* Waitlists already joined, across every connected Dice account */}
      <section aria-label="My waitlists" className="mb-6">
        <MyWaitlistsSection
          accountsPath={v2Path('/accounts')}
          billingPath={v2Path('/billing')}
        />
      </section>

      {/* Join defaults — the answers to the two questions v1 re-asked per event.
          Stated out loud, because a silent default is worse than a visible one. */}
      <section
        aria-labelledby="join-defaults-heading"
        data-tour="waitlist-defaults"
        className="mb-6 rounded-2xl border border-[#3b82f6]/20 bg-[#3b82f6]/[0.04] p-4 sm:p-5"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="join-defaults-heading" className="text-sm font-semibold text-white">
              Join defaults
            </h2>
            <p className="mt-0.5 text-xs text-white/50">
              Used by every one-tap join below. Updated each time you join.
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Badge tone="blue">{defaultsSummary}</Badge>
              <Badge tone="blue">{ticketsLabel(joinDefaults.quantity)} each</Badge>
            </div>
          </div>

          <button
            type="button"
            aria-expanded={defaultsOpen}
            aria-controls="join-defaults-editor"
            onClick={() => setDefaultsOpen(prev => !prev)}
            className={cx(
              'flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white/80 transition-colors hover:bg-white/10',
              FOCUS_RING,
            )}
          >
            {defaultsOpen ? 'Done' : 'Change'}
            <ChevronDownIcon
              className={cx('h-4 w-4 transition-transform', defaultsOpen && 'rotate-180')}
            />
          </button>
        </div>

        {defaultsOpen && (
          <div
            id="join-defaults-editor"
            className="mt-4 space-y-3 border-t border-white/[0.08] pt-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <SegmentedControl
                label="Default accounts to join with"
                size="sm"
                value={joinDefaults.mode}
                onChange={(v) => setJoinDefaults(prev => ({ ...prev, mode: v }))}
                options={segmentOptions}
              />
              <select
                value={joinDefaults.quantity}
                onChange={e => setJoinDefaults(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                aria-label="Default tickets per account"
                className={cx(SELECT_CLASS, 'w-full py-1.5 sm:w-auto')}
              >
                {defaultQuantityOptions.map(q => (
                  <option key={q} value={q}>
                    {ticketsLabel(q)}
                  </option>
                ))}
              </select>
            </div>

            {joinDefaults.mode === 'group' && (
              <GroupPicker
                picks={groupPicks}
                selectedId={joinDefaults.groupId}
                onPick={(id) => setJoinDefaults(prev => ({ ...prev, groupId: id }))}
                accountsPath={v2Path('/accounts')}
              />
            )}

            {joinDefaults.mode === 'select' && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-white/40">Default accounts</span>
                  <button
                    type="button"
                    onClick={() => setJoinDefaults(prev => ({
                      ...prev,
                      accountIds: diceAccounts.map(a => a.id),
                    }))}
                    className={cx('rounded px-1.5 py-0.5 text-xs text-[#60a5fa] hover:underline', FOCUS_RING)}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setJoinDefaults(prev => ({ ...prev, accountIds: [] }))}
                    className={cx('rounded px-1.5 py-0.5 text-xs text-white/50 hover:underline', FOCUS_RING)}
                  >
                    Clear
                  </button>
                  {groupPicks.length > 0 && (
                    <>
                      <span className="text-xs text-white/30">·</span>
                      <span className="text-xs text-white/40">Use group</span>
                      {groupPicks.map(({ group, ids }) => (
                        <AccountGroupChip
                          key={group.id}
                          group={group}
                          count={ids.length}
                          active={false}
                          title={`Select the ${ids.length} DICE account${ids.length !== 1 ? 's' : ''} in ${group.name}`}
                          onClick={() => setJoinDefaults(prev => ({ ...prev, accountIds: ids }))}
                        />
                      ))}
                    </>
                  )}
                </div>
                <div className="grid gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-2">
                  {diceAccounts.map(acct => {
                    const isChecked = defaultAccountIds.includes(acct.id);
                    return (
                      <label key={acct.id} className="flex min-w-0 cursor-pointer items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => setJoinDefaults(prev => {
                            const next = new Set(prev.accountIds.filter(id => diceAccountIds.has(id)));
                            if (next.has(acct.id)) next.delete(acct.id);
                            else next.add(acct.id);
                            return { ...prev, accountIds: Array.from(next) };
                          })}
                          className={cx('h-4 w-4 flex-shrink-0 cursor-pointer accent-[#3b82f6]', FOCUS_RING)}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm text-white/80">
                          {accountLabel(acct)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <p className="text-xs text-white/40">
              Each event snaps this to the closest quantity its ticket tier allows. Open
              <span className="text-white/60"> Options </span>
              on any event to override it there only.
            </p>
          </div>
        )}

        {diceAccounts.length === 0 && (
          <p className="mt-3 text-xs text-amber-400/80">
            No active DICE accounts yet — joining will take you to Accounts to connect one.
          </p>
        )}
      </section>

      {/* Filters — the search field is never inside a disclosure (§4.4) */}
      <div className="mb-6 space-y-3">
        <div className="relative" data-tour="waitlist-search">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search events, venues, promoters..."
            className={cx(
              'w-full rounded-lg border border-white/10 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white placeholder-white/30 focus:border-[#3b82f6]/50 focus:outline-none',
              FOCUS_RING,
            )}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            aria-pressed={waitlistOnly}
            className={filterBtnClass(waitlistOnly)}
            onClick={() => setWaitlistOnly(prev => !prev)}
          >
            Waitlist Available
          </button>
          <button
            type="button"
            aria-pressed={subscribedOnly}
            className={filterBtnClass(subscribedOnly)}
            onClick={() => setSubscribedOnly(prev => !prev)}
          >
            Subscribed
          </button>
          <select
            value={cityFilter}
            onChange={e => setCityFilter(e.target.value)}
            aria-label="City"
            className={cx(SELECT_CLASS, 'py-1.5')}
          >
            <option value="">All Cities</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredEvents.length === 0 && (
        <EmptyState
          icon={<ListIcon className="h-5 w-5" />}
          title="No sold-out events found"
          body="Try adjusting your filters or check back later"
        />
      )}

      {/* Event cards */}
      {!isLoading && filteredEvents.length > 0 && (
        <div className="space-y-4">
          {filteredEvents.map((event, index) => {
            const eventId = event.event_id;
            const plan = resolvePlan(event);
            const {
              openTypes,
              ticketType,
              quantityOptions,
              mode,
              isUnjoinedMode,
              quantity,
              accountIds,
              skippedWithTickets,
              hasOverride,
            } = plan;
            const hasActiveJob = !!activeJobIds[eventId];
            const isExpanded = !!expandedEvents[eventId];
            const panelId = `waitlist-options-${event.id}`;
            const selectedTtId = ticketType?.id;
            const canJoin = !!ticketType && (mode === 'unjoined' || accountIds.length > 0);
            const previous = index > 0 ? filteredEvents[index - 1] : undefined;
            const startsClosedGroup =
              openTypes.length === 0 && !!previous && openTicketTypes(previous).length > 0;

            const primaryLabel = bulkJoinMutation.isPending
              ? 'Joining...'
              : hasActiveJob
              ? 'Job Running...'
              : isUnjoinedMode
              // "up to": accounts already on the list drop out, and quantities
              // get trimmed, neither of which is known until the click — so the
              // label promises a ceiling rather than a count.
              ? `Join remaining (up to ${diceAccounts.length}) · ${ticketsLabel(quantity)}`
              : mode === 'group'
              ? plan.groupPick
                ? `Join ${plan.groupPick.group.name} (up to ${accountIds.length}) · ${ticketsLabel(quantity)}`
                : 'Pick a group to join'
              : `Join ${accountIds.length} selected · ${ticketsLabel(quantity)}`;

            return (
              <React.Fragment key={event.id}>
                {startsClosedGroup && (
                  <h2 className="pt-2 text-xs font-medium uppercase tracking-wide text-white/30">
                    No waitlist open yet
                  </h2>
                )}

                <article className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5">
                  {/* Zone A — identity, and the one-action join beside it */}
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
                    <div className="flex min-w-0 gap-3 sm:gap-4">
                      {event.image_url && (
                        <img
                          src={event.image_url}
                          alt={event.name}
                          className="h-16 w-16 flex-shrink-0 rounded-lg object-cover sm:h-24 sm:w-24"
                        />
                      )}

                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        {event.source_url ? (
                          <a
                            href={event.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cx(
                              'truncate rounded font-semibold text-white transition-colors hover:text-blue-400',
                              FOCUS_RING,
                            )}
                          >
                            {event.name}
                          </a>
                        ) : (
                          <span className="truncate font-semibold text-white">{event.name}</span>
                        )}

                        <span className="text-sm text-white/60">
                          {formatVenueLocalDate(event.event_start_date)}
                          {event.venue_name && ` · ${event.venue_name}`}
                          {event.venue_city && ` · ${event.venue_city}`}
                        </span>

                        {event.promoter_name && (
                          <span className="text-sm text-white/40">{event.promoter_name}</span>
                        )}

                        {/* What the one-tap join is aimed at. With a single open
                            tier there is nothing to decide, so we state it; with
                            several, the choice sits right here. */}
                        {openTypes.length === 1 && ticketType && (
                          <span className="mt-1 flex flex-wrap items-center gap-2 text-sm text-white/60">
                            <span className="truncate">{ticketType.display_name}</span>
                            <span className="tabular-nums">
                              {formatPrice(ticketType.price_amount, ticketType.currency)}
                            </span>
                            <Badge tone="green" size="sm">Waitlist Open</Badge>
                          </span>
                        )}

                        {openTypes.length > 1 && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <select
                              value={selectedTtId ?? ''}
                              onChange={e => setSelectedTicketType(prev => ({
                                ...prev,
                                [eventId]: Number(e.target.value),
                              }))}
                              aria-label="Ticket type to join"
                              className={cx(SELECT_CLASS, 'max-w-full py-1 text-xs')}
                            >
                              {openTypes.map(tt => (
                                <option key={tt.id} value={tt.id}>
                                  {tt.display_name} — {formatPrice(tt.price_amount, tt.currency)}
                                </option>
                              ))}
                            </select>
                            <Badge tone="green" size="sm">Waitlist Open</Badge>
                          </div>
                        )}

                        {openTypes.length === 0 && (
                          <span className="mt-1">
                            <Badge tone="red" size="sm">Sold Out</Badge>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* The common path: one action, fully labelled. */}
                    <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-shrink-0 sm:items-end">
                      {openTypes.length > 0 && (
                        <div className="[&_button]:w-full sm:[&_button]:w-auto">
                          <Button
                            variant="primary"
                            size="sm"
                            whitespace="nowrap"
                            onClick={() => handleBulkJoin(eventId)}
                            disabled={bulkJoinMutation.isPending || hasActiveJob || !canJoin}
                          >
                            {primaryLabel}
                          </Button>
                        </div>
                      )}

                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={panelId}
                        onClick={() => {
                          setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
                          setStatusEventIds(prev => (prev.includes(eventId) ? prev : [...prev, eventId]));
                        }}
                        className={cx(
                          'flex items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70 transition-colors hover:bg-white/10',
                          FOCUS_RING,
                        )}
                      >
                        Options
                        <ChevronDownIcon
                          className={cx('h-3.5 w-3.5 transition-transform', isExpanded && 'rotate-180')}
                        />
                      </button>

                      {hasOverride && (
                        <span className="text-center text-[11px] text-amber-400/80 sm:text-right">
                          Custom for this event
                        </span>
                      )}

                      {mode === 'select' && accountIds.length === 0 && openTypes.length > 0 && (
                        <span className="text-center text-[11px] text-amber-400/80 sm:text-right">
                          No accounts selected
                        </span>
                      )}

                      {mode === 'group' && !plan.groupPick && openTypes.length > 0 && (
                        <span className="text-center text-[11px] text-amber-400/80 sm:text-right">
                          No group picked — open Options
                        </span>
                      )}

                      {mode === 'select' && skippedWithTickets > 0 && openTypes.length > 0 && (
                        <span className="text-center text-[11px] text-blue-300/80 sm:text-right">
                          {skippedWithTickets} skipped — already {skippedWithTickets === 1 ? 'has' : 'have'} tickets
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Zone B + C — per-event overrides, on demand only */}
                  {isExpanded && (
                    <div id={panelId} className="mt-4 space-y-4 border-t border-white/[0.06] pt-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-white/40">
                          This event only
                        </span>
                        {hasOverride && (
                          <button
                            type="button"
                            onClick={() => clearOverrides(eventId)}
                            className={cx('rounded px-1.5 py-0.5 text-xs text-[#60a5fa] hover:underline', FOCUS_RING)}
                          >
                            Reset to defaults
                          </button>
                        )}
                      </div>

                      {/* Ticket types — every tier, including the sold-out ones */}
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {event.ticket_types.map(tt => {
                          const isSelected = selectedTtId === tt.id;
                          const hasWaitlist = isWaitlistOpen(tt);

                          return (
                            <button
                              key={tt.id}
                              type="button"
                              aria-pressed={isSelected}
                              disabled={!hasWaitlist}
                              onClick={() => {
                                if (!hasWaitlist) return;
                                setSelectedTicketType(prev => ({ ...prev, [eventId]: tt.id }));
                              }}
                              className={cx(
                                'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all',
                                !hasWaitlist
                                  ? 'border-white/5 bg-white/[0.01] opacity-50 cursor-not-allowed'
                                  : isSelected
                                  ? 'border-[#3b82f6] bg-[#3b82f6]/10'
                                  : 'border-white/10 bg-white/[0.02] hover:bg-white/5',
                                FOCUS_RING,
                              )}
                            >
                              <span className="min-w-0 flex-1 truncate text-sm text-white/80">{tt.display_name}</span>
                              <span className="flex-shrink-0 text-sm text-white/60 tabular-nums">
                                {formatPrice(tt.price_amount, tt.currency)}
                              </span>
                              {hasWaitlist ? (
                                <Badge tone="green" size="sm">Waitlist Open</Badge>
                              ) : (
                                <Badge tone="red" size="sm">Sold Out</Badge>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Join panel */}
                      {ticketType && (
                        <div className="space-y-3">
                          <SegmentedControl
                            label="Accounts to join with"
                            size="sm"
                            value={mode}
                            onChange={(v) => setJoinMode(prev => ({ ...prev, [eventId]: v as JoinMode }))}
                            options={segmentOptions}
                          />

                          {/* Mode 1: Remaining Accounts */}
                          {isUnjoinedMode && (
                            <div className="space-y-2">
                              <p className="text-[11px] text-white/40">
                                {event.max_tickets != null
                                  ? `Skips accounts already on this waitlist, and asks each of the rest for ${ticketsLabel(quantity)} or whatever this event's ${event.max_tickets}-ticket limit leaves them — whichever is smaller.`
                                  : `Skips accounts already on this waitlist or already holding tickets for this event, and asks the rest for ${ticketsLabel(quantity)}. This event declares no ticket limit, so a partial top-up cannot be sized.`}
                              </p>
                            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                              <select
                                value={quantity}
                                onChange={e => setSelectedQuantity(prev => ({
                                  ...prev,
                                  [eventId]: Number(e.target.value),
                                }))}
                                aria-label="Tickets per account"
                                className={cx(SELECT_CLASS, 'w-full py-1.5 sm:w-auto')}
                              >
                                {quantityOptions.map(q => (
                                  <option key={q} value={q}>
                                    {ticketsLabel(q)}
                                  </option>
                                ))}
                              </select>
                              <div className="[&_button]:w-full sm:[&_button]:w-auto">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleBulkJoin(eventId)}
                                  disabled={bulkJoinMutation.isPending || hasActiveJob}
                                >
                                  {bulkJoinMutation.isPending
                                    ? 'Joining...'
                                    : hasActiveJob ? 'Job Running...'
                                    : `Join Remaining (up to ${diceAccounts.length} account${diceAccounts.length !== 1 ? 's' : ''})`}
                                </Button>
                              </div>
                            </div>
                            </div>
                          )}

                          {/* Mode 2: Group — one group, one quantity, one button. */}
                          {mode === 'group' && (
                            <div className="space-y-3">
                              <GroupPicker
                                picks={groupPicks}
                                selectedId={plan.groupPick?.group.id ?? null}
                                onPick={(id) => setJoinGroup(prev => ({ ...prev, [eventId]: id }))}
                                accountsPath={v2Path('/accounts')}
                              />
                              {plan.groupPick && (
                                <p className="text-[11px] text-white/40">
                                  Skips members of {plan.groupPick.group.name} already on this waitlist
                                  {event.max_tickets != null ? ' or at the ticket limit' : ''}, and asks each of the rest for {ticketsLabel(quantity)}.
                                </p>
                              )}
                              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                                <select
                                  value={quantity}
                                  onChange={e => setSelectedQuantity(prev => ({
                                    ...prev,
                                    [eventId]: Number(e.target.value),
                                  }))}
                                  aria-label="Tickets per account"
                                  className={cx(SELECT_CLASS, 'w-full py-1.5 sm:w-auto')}
                                >
                                  {quantityOptions.map(q => (
                                    <option key={q} value={q}>
                                      {ticketsLabel(q)}
                                    </option>
                                  ))}
                                </select>
                                <div className="[&_button]:w-full sm:[&_button]:w-auto">
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleBulkJoin(eventId)}
                                    disabled={bulkJoinMutation.isPending || hasActiveJob || !plan.groupPick}
                                  >
                                    {bulkJoinMutation.isPending
                                      ? 'Joining...'
                                      : hasActiveJob ? 'Job Running...'
                                      : plan.groupPick
                                      ? `Join ${plan.groupPick.group.name} (up to ${accountIds.length} account${accountIds.length !== 1 ? 's' : ''})`
                                      : 'Pick a group'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Mode 3: Select Accounts */}
                          {mode === 'select' && (() => {
                            const lookup = lookupFor(eventId);
                            return (
                            <div className="space-y-2">
                              {/* The sync line stays up even when nothing is
                                  flagged: "nothing to warn about" is only as
                                  true as the last account sync. */}
                              {!lookup.isLoading && (
                                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                  <p className="text-[11px] text-white/40">
                                    {lookup.onWaitlistCount > 0 && (
                                      <span className="text-amber-400/80">
                                        {lookup.onWaitlistCount} account{lookup.onWaitlistCount === 1 ? ' is' : 's are'} already
                                        on this waitlist ·{' '}
                                      </span>
                                    )}
                                    {lookup.withTicketsCount > 0 && (
                                      <span className="text-blue-300/80">
                                        {lookup.withTicketsCount} already
                                        {lookup.withTicketsCount === 1 ? ' has' : ' have'} tickets ·{' '}
                                      </span>
                                    )}
                                    {lookup.syncedLabel}
                                  </p>
                                  {/* The counts above are the summary; this
                                      opens the per-row version of the same
                                      thing, which is a lot of ink on a ten-row
                                      list nobody asked to read. */}
                                  {lookup.withTicketsCount > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setShowTicketDetails(v => !v)}
                                      aria-expanded={showTicketDetails}
                                      className={cx(
                                        'rounded text-[11px] text-blue-400 transition-colors hover:text-blue-300',
                                        FOCUS_RING,
                                      )}
                                    >
                                      {showTicketDetails ? 'Hide ticket details' : 'Show ticket details'}
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* Group quick picks: replaces this event's
                                  selection with the group's usable members.
                                  An explicit selection, so nothing is trimmed
                                  for ticket ownership afterwards — the badges
                                  below still say who already holds tickets. */}
                              {groupPicks.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs text-white/40">Use group</span>
                                  {groupPicks.map(({ group, ids }) => {
                                    const isExact =
                                      ids.length === accountIds.length && ids.every(id => accountIds.includes(id));
                                    return (
                                      <AccountGroupChip
                                        key={group.id}
                                        group={group}
                                        count={ids.length}
                                        active={isExact}
                                        title={`Select the ${ids.length} DICE account${ids.length !== 1 ? 's' : ''} in ${group.name}`}
                                        onClick={() => setSelectedAccounts(prev => ({ ...prev, [eventId]: new Set(ids) }))}
                                      />
                                    );
                                  })}
                                </div>
                              )}
                              <div className="grid gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 sm:grid-cols-2">
                                {lookup.sortAccounts(diceAccounts).map(acct => {
                                  const isChecked = accountIds.includes(acct.id);
                                  const acctQty = perAccountQty[eventId]?.[acct.id] || quantity;
                                  const badges = lookup.badgesFor(acct.id, {
                                    selectedTicketTypeName: ticketType?.display_name,
                                    includeTickets: showTicketDetails,
                                  });

                                  return (
                                    <div key={acct.id} className="flex min-w-0 flex-col gap-1">
                                      <div className="flex min-w-0 items-center gap-3">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() => {
                                            setSelectedAccounts(prev => {
                                              const current = new Set(prev[eventId] ?? accountIds);
                                              if (current.has(acct.id)) current.delete(acct.id);
                                              else current.add(acct.id);
                                              return { ...prev, [eventId]: current };
                                            });
                                          }}
                                          aria-label={accountIdentity(acct)}
                                          className={cx('h-4 w-4 flex-shrink-0 cursor-pointer accent-[#3b82f6]', FOCUS_RING)}
                                        />
                                        <span className="min-w-0 flex-1 truncate text-sm text-white/80">
                                          {accountIdentity(acct)}
                                        </span>
                                        <select
                                          value={resolveQuantity(quantityOptions, acctQty)}
                                          onChange={e => setPerAccountQty(prev => ({
                                            ...prev,
                                            [eventId]: {
                                              ...prev[eventId],
                                              [acct.id]: Number(e.target.value),
                                            },
                                          }))}
                                          aria-label="Tickets for this account"
                                          className={cx(SELECT_CLASS, 'flex-shrink-0 px-2 py-1')}
                                        >
                                          {quantityOptions.map(q => (
                                            <option key={q} value={q}>{q}</option>
                                          ))}
                                        </select>
                                      </div>
                                      {/* min-w-0 + wrapping keeps the warnings inside their grid
                                          column instead of spilling over the next one. */}
                                      {badges.map(badge => (
                                        <div key={badge.label} className="min-w-0 pl-7">
                                          <Badge tone={badge.tone === 'warn' ? 'amber' : badge.tone === 'info' ? 'blue' : 'neutral'}>
                                            {badge.label}
                                          </Badge>
                                          {badge.detail && (
                                            <p className="mt-0.5 break-words text-[11px] leading-snug text-white/40">
                                              {badge.detail}
                                            </p>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="[&_button]:w-full sm:[&_button]:w-auto">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleBulkJoin(eventId)}
                                  disabled={bulkJoinMutation.isPending || hasActiveJob || accountIds.length === 0}
                                >
                                  {bulkJoinMutation.isPending
                                    ? 'Joining...'
                                    : hasActiveJob ? 'Job Running...'
                                    : `Join Waitlist (${accountIds.length} selected)`}
                                </Button>
                              </div>
                            </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Dice mails a code to one account after the waitlist
                      draws. It lands here beside the join because that is where
                      the seller already is with this event in front of them;
                      the same claim also sits on each "My waitlists" group. */}
                  <WaitlistClaimCode
                    eventId={eventId}
                    accounts={claimAccountOptions}
                    accountsPath={v2Path('/accounts')}
                    billingPath={v2Path('/billing')}
                  />

                  {/* Zone D — inline job status */}
                  {hasActiveJob && (
                    <div className="mt-3">
                      <JobStatusBanner
                        jobId={activeJobIds[eventId]}
                        onProgress={job => badgeJoinedAccounts(eventId, job)}
                        onComplete={handleJobComplete(eventId)}
                        onCancel={handleJobCancel(eventId)}
                      />
                    </div>
                  )}
                </article>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Replace-warning dialog. Only opens when joining would cost something:
          entries that Dice would overwrite. Skipping is never escalated to a
          dialog — a mode whose job is to skip should not ask permission. */}
      {joinConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-join-confirm-title"
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#0f0f0f] p-6"
          >
            <h2 id="v2-join-confirm-title" className="mb-1 text-lg font-medium text-white">
              {joinConfirm.onWaitlist.length} account
              {joinConfirm.onWaitlist.length === 1 ? ' is' : 's are'} already on this waitlist
            </h2>
            <p className="mb-4 text-sm text-white/40">
              Dice keeps one entry per account. Joining again replaces the ticket type each of
              these is on now — it does not add a second entry.
            </p>

            <ul className="mb-6 space-y-1 text-sm text-white/70">
              {joinConfirm.onWaitlist.map(id => {
                const acct = diceAccounts.find(a => a.id === id);
                return (
                  <li key={id} className="truncate">
                    {acct ? accountLabel(acct) : `Account ${id}`}
                    <span className="text-white/40"> — on this waitlist</span>
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setJoinConfirm(null)}
                className={cx(
                  'flex-1 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5',
                  FOCUS_RING,
                )}
              >
                Cancel
              </button>

              {joinConfirm.eligible.length > 0 && (
                <button
                  type="button"
                  onClick={() => resolveJoinConfirm(joinConfirm.eligible)}
                  className={cx(
                    'flex-1 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5',
                    FOCUS_RING,
                  )}
                >
                  Join the other {joinConfirm.eligible.length}
                </button>
              )}

              <button
                type="button"
                onClick={() =>
                  resolveJoinConfirm([...joinConfirm.eligible, ...joinConfirm.onWaitlist])
                }
                className={cx(
                  'flex-1 rounded-xl bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90',
                  FOCUS_RING_SURFACE,
                )}
              >
                Join {joinConfirm.eligible.length + joinConfirm.onWaitlist.length} anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};
